/**
 * UserManagement component for managing users in the admin panel.
 * 
 * Features:
 * - Table with user list
 * - Filter by status (pending/approved/all) and role
 * - Approve/reject pending users
 * - Change user roles
 * - Activity stats (comments, suggestions)
 * 
 * Called by:
 * - AdminPage.tsx Users tab
 */
import { useState } from 'react';
import {
  Table,
  Avatar,
  Tag,
  Space,
  Button,
  Select,
  Popconfirm,
  message,
  Typography,
  Input,
  Spin,
  Empty,
} from 'antd';
import {
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  CommentOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserWithStats, UserRole } from '@compendium/shared';
import { getUsers, approveUser, rejectUser, changeUserRole, blockUser, unblockUser } from '../../services/api';

const { Text } = Typography;

// =============================================================================
// Role Colors
// =============================================================================

const ROLE_COLORS: Record<UserRole, string> = {
  author: 'purple',
  editor: 'blue',
  reviewer: 'default',
  commenter: 'green',
};

const ROLE_LABELS: Record<UserRole, string> = {
  author: 'Author',
  editor: 'Editor',
  reviewer: 'Reviewer',
  commenter: 'Commenter',
};

// =============================================================================
// Component Props
// =============================================================================

interface UserManagementProps {
  /** Filter to show only pending users */
  pendingOnly?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * UserManagement component.
 * Displays user table with management actions.
 */
function UserManagement({ pendingOnly = false }: UserManagementProps) {
  const queryClient = useQueryClient();
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'blocked' | 'all'>(
    pendingOnly ? 'pending' : 'all'
  );
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  
  // Loading states for actions
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [changingRoleId, setChangingRoleId] = useState<number | null>(null);
  const [blockingId, setBlockingId] = useState<number | null>(null);

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', statusFilter, roleFilter],
    queryFn: () => getUsers(statusFilter, roleFilter),
  });

  /**
   * Format date for display.
   */
  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Handle approve user.
   */
  const handleApprove = async (userId: number) => {
    setApprovingId(userId);
    try {
      await approveUser(userId);
      message.success('User approved successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      console.error('Failed to approve user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to approve user');
    } finally {
      setApprovingId(null);
    }
  };

  /**
   * Handle reject user.
   */
  const handleReject = async (userId: number) => {
    setRejectingId(userId);
    try {
      await rejectUser(userId);
      message.success('User rejected and removed');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      console.error('Failed to reject user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to reject user');
    } finally {
      setRejectingId(null);
    }
  };

  /**
   * Handle role change.
   */
  const handleRoleChange = async (userId: number, newRole: UserRole) => {
    setChangingRoleId(userId);
    try {
      await changeUserRole(userId, newRole);
      message.success(`Role changed to ${ROLE_LABELS[newRole]}`);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error) {
      console.error('Failed to change role:', error);
      message.error(error instanceof Error ? error.message : 'Failed to change role');
    } finally {
      setChangingRoleId(null);
    }
  };

  /**
   * Handle block user.
   */
  const handleBlock = async (userId: number) => {
    setBlockingId(userId);
    try {
      await blockUser(userId);
      message.success('User blocked');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      console.error('Failed to block user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to block user');
    } finally {
      setBlockingId(null);
    }
  };

  /**
   * Handle unblock user.
   */
  const handleUnblock = async (userId: number) => {
    setBlockingId(userId);
    try {
      await unblockUser(userId);
      message.success('User unblocked');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    } catch (error) {
      console.error('Failed to unblock user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to unblock user');
    } finally {
      setBlockingId(null);
    }
  };

  // Filter users by search text
  const filteredUsers = usersData?.users.filter((user) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      user.name.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search)
    );
  }) ?? [];

  // Table columns
  const columns: ColumnsType<UserWithStats> = [
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space style={{ whiteSpace: 'nowrap' }}>
          <Avatar
            src={record.avatarUrl}
            icon={!record.avatarUrl && <UserOutlined />}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ whiteSpace: 'nowrap' }}>
              <Text strong>{record.name}</Text>
            </div>
            <div style={{ whiteSpace: 'nowrap' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.email}
              </Text>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 150,
      render: (_, record) => {
        // Author role cannot be changed
        if (record.role === 'author') {
          return <Tag color={ROLE_COLORS.author}>{ROLE_LABELS.author}</Tag>;
        }
        
        return (
          <Select
            value={record.role}
            onChange={(value) => handleRoleChange(record.id, value)}
            loading={changingRoleId === record.id}
            disabled={changingRoleId === record.id}
            style={{ width: 120 }}
            size="small"
          >
            <Select.Option value="commenter">
              <Tag color={ROLE_COLORS.commenter}>{ROLE_LABELS.commenter}</Tag>
            </Select.Option>
            <Select.Option value="reviewer">
              <Tag color={ROLE_COLORS.reviewer}>{ROLE_LABELS.reviewer}</Tag>
            </Select.Option>
            <Select.Option value="editor">
              <Tag color={ROLE_COLORS.editor}>{ROLE_LABELS.editor}</Tag>
            </Select.Option>
          </Select>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (record.status === 'blocked') return <Tag color="error">Blocked</Tag>;
        if (record.status === 'approved') return <Tag color="success">Approved</Tag>;
        return <Tag color="warning">Pending</Tag>;
      },
    },
    {
      title: 'Activity',
      key: 'activity',
      width: 150,
      responsive: ['md', 'lg', 'xl', 'xxl'] as any,
      render: (_, record) => (
        <Space>
          <Tag icon={<CommentOutlined />} color="cyan">
            {record.stats.commentsCount}
          </Tag>
          <Tag icon={<BulbOutlined />} color="magenta">
            {record.stats.suggestionsCount}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Joined',
      key: 'createdAt',
      width: 120,
      responsive: ['md', 'lg', 'xl', 'xxl'] as any,
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDate(record.createdAt)}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        // No actions for author
        if (record.role === 'author') {
          return <Text type="secondary">—</Text>;
        }

        // Blocked users: unblock or remove
        if (record.status === 'blocked') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={blockingId === record.id}
                onClick={() => handleUnblock(record.id)}
              >
                Unblock
              </Button>
              <Popconfirm
                title="Remove this user?"
                description="This will permanently delete their account."
                onConfirm={() => handleReject(record.id)}
                okText="Remove"
                okType="danger"
                cancelText="Cancel"
              >
                <Button
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  loading={rejectingId === record.id}
                >
                  Remove
                </Button>
              </Popconfirm>
            </Space>
          );
        }

        // Pending users: approve/reject
        if (record.status === 'pending') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={approvingId === record.id}
                onClick={() => handleApprove(record.id)}
              >
                Approve
              </Button>
              <Popconfirm
                title="Reject this user?"
                description="This will permanently delete their account."
                onConfirm={() => handleReject(record.id)}
                okText="Reject"
                okType="danger"
                cancelText="Cancel"
              >
                <Button
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  loading={rejectingId === record.id}
                >
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        }

        // Approved users: block or remove
        return (
          <Space>
            <Popconfirm
              title="Block this user?"
              description="They will no longer be able to comment."
              onConfirm={() => handleBlock(record.id)}
              okText="Block"
              okType="danger"
              cancelText="Cancel"
            >
              <Button
                size="small"
                icon={<CloseOutlined />}
                loading={blockingId === record.id}
              >
                Block
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Remove this user?"
              description="This will permanently delete their account."
              onConfirm={() => handleReject(record.id)}
              okText="Remove"
              okType="danger"
              cancelText="Cancel"
            >
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                loading={rejectingId === record.id}
              >
                Remove
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Empty
        description={error instanceof Error ? error.message : 'Failed to load users'}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {/* Filters */}
      {!pendingOnly && (
        <Space style={{ marginBottom: '16px' }} wrap>
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 120 }}
          >
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="approved">Approved</Select.Option>
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="blocked">Blocked</Select.Option>
          </Select>
          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            style={{ width: 120 }}
            placeholder="All Roles"
            allowClear
          >
            <Select.Option value="editor">Editor</Select.Option>
            <Select.Option value="reviewer">Reviewer</Select.Option>
          </Select>
        </Space>
      )}

      {/* Summary */}
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
          {usersData?.pending ? ` (${usersData.pending} pending)` : ''}
        </Text>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        scroll={{ x: 500 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`,
        }}
        style={{
          background: '#1a1a1a',
        }}
      />
    </div>
  );
}

export default UserManagement;
